// PnL Summary Card Component
// Displays performance summary with gains, losses, PnL, win rate, and best/worst trades
import React from 'react';
import { DollarSign, Trophy, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const PnlSummaryCard = ({
  language,
  totalGains,
  totalLosses,
  totalPnl,
  winRate,
  bestTrade,
  worstTrade,
  variant = 'real' // 'real' or 'paper'
}) => {
  const isPaper = variant === 'paper';
  const iconColor = isPaper ? 'text-purple-400' : 'text-primary';

  return (
    <Card className="bg-gradient-to-r from-card to-card/50 border-white/10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className={`w-5 h-5 ${iconColor}`} />
          {language === 'fr' ? 'Récapitulatif Performance' : 'Performance Summary'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Total Gains */}
          <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
            <div className="text-sm text-muted-foreground mb-1">
              {language === 'fr' ? 'Gains Totaux' : 'Total Gains'}
            </div>
            <div className="text-2xl font-mono font-bold text-primary">
              +${totalGains.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </div>
          </div>
          
          {/* Total Losses */}
          <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
            <div className="text-sm text-muted-foreground mb-1">
              {language === 'fr' ? 'Pertes Totales' : 'Total Losses'}
            </div>
            <div className="text-2xl font-mono font-bold text-destructive">
              -${totalLosses.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </div>
          </div>
          
          {/* Final PNL */}
          <div className={`p-4 rounded-lg border ${totalPnl >= 0 ? 'bg-primary/20 border-primary/30' : 'bg-destructive/20 border-destructive/30'}`}>
            <div className="text-sm text-muted-foreground mb-1">
              {language === 'fr' ? 'PNL Final' : 'Final PNL'}
            </div>
            <div className={`text-2xl font-mono font-bold ${totalPnl >= 0 ? 'text-primary' : 'text-destructive'}`}>
              {totalPnl >= 0 ? '+' : ''}{totalPnl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}$
            </div>
          </div>
          
          {/* Win Rate */}
          <div className="p-4 rounded-lg bg-secondary/50 border border-white/10">
            <div className="text-sm text-muted-foreground mb-1">
              {language === 'fr' ? 'Taux de Réussite' : 'Win Rate'}
            </div>
            <div className="text-2xl font-mono font-bold">
              {winRate}%
            </div>
          </div>
        </div>
        
        {/* Best & Worst Trades */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Best Trade */}
          {bestTrade && bestTrade.pnl > 0 && (
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                <span className="font-medium text-primary">
                  {language === 'fr' ? 'Meilleur Trade' : 'Best Trade'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-mono font-bold">{bestTrade.symbol}</span>
                  <span className={`ml-2 text-sm ${bestTrade.direction === 'LONG' ? 'text-primary' : 'text-destructive'}`}>
                    {bestTrade.direction}
                  </span>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-primary">
                    +${bestTrade.pnl?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    +{bestTrade.pnl_percentage?.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Worst Trade */}
          {worstTrade && worstTrade.pnl < 0 && (
            <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                <span className="font-medium text-destructive">
                  {language === 'fr' ? 'Pire Trade' : 'Worst Trade'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-mono font-bold">{worstTrade.symbol}</span>
                  <span className={`ml-2 text-sm ${worstTrade.direction === 'LONG' ? 'text-primary' : 'text-destructive'}`}>
                    {worstTrade.direction}
                  </span>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-destructive">
                    ${worstTrade.pnl?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {worstTrade.pnl_percentage?.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PnlSummaryCard;
